import React, { useState } from 'react'

interface SortConfig {
    key: string;
    direction: 'ascending' | 'descending' | null;
}

export default function useTableSort({
    defaultSortConfig = {
        key: '',
        direction: null
    }
}: {
    defaultSortConfig?: SortConfig
}) {
    const [sortConfig, setSortConfig] = useState<SortConfig>(defaultSortConfig || { key: '', direction: null });

    const getLengthOfCommaSeparatedValues = (value: string) => {
        try {
            if (typeof value === 'string' && value?.length > 0) {
                return value.split(',').length;
            }
            return 0;
        } catch (error) {
            console.error('Error in getLengthOfCommaSeparatedValues:', error);
            return 0;
        }
    }

    function sortData(data: any[]) {
        try {
            if (!sortConfig.key || sortConfig.direction === null) {
                return data;
            }

            const sortableData = [...data];

            return sortableData.sort((a, b) => {
                // Skip sorting for group header rows
                if (a.hasOwnProperty('groupName') || b.hasOwnProperty('groupName')) {
                    return 0;
                }

                // Get values to compare
                let aValue = a[sortConfig.key] || '';
                let bValue = b[sortConfig.key] || '';

                if (sortConfig.key === 'SkillsFormatted') {
                    try {
                        let tempA = a.AgentSkills || ""
                        let tempB = b.AgentSkills || ""
                        if (tempA.length > 0) {
                            aValue = getLengthOfCommaSeparatedValues(tempA)
                        }
                        if (tempB.length > 0) {
                            bValue = getLengthOfCommaSeparatedValues(tempB)
                        }
                    } catch (error) {
                        console.error('Error in sortData skills:', error);
                    }
                }

                // Handle complex objects (for fields with warning)
                if (typeof aValue === 'object' && aValue !== null) {
                    aValue = aValue.value || '';
                }
                if (typeof bValue === 'object' && bValue !== null) {
                    bValue = bValue.value || '';
                }

                // Convert to appropriate types for comparison
                if (!isNaN(Number(aValue)) && !isNaN(Number(bValue))) {
                    // Handle numbers
                    aValue = Number(aValue);
                    bValue = Number(bValue);
                } else if (typeof aValue === 'string' && typeof bValue === 'string') {
                    // Handle strings with case-insensitive comparison
                    aValue = aValue.toLowerCase();
                    bValue = bValue.toLowerCase();
                }

                // Sort based on direction
                if (aValue < bValue) {
                    return sortConfig.direction === 'ascending' ? -1 : 1;
                }
                if (aValue > bValue) {
                    return sortConfig.direction === 'ascending' ? 1 : -1;
                }

                return 0;
            });
        } catch (error) {
            console.error('Error sorting data:', error);
            return data;
        }
    }

    function getSortedGroupData(data: any, finalTableData: any) {
        try {
            const sortedGroupData = [...data];
            if (sortConfig.key && sortConfig.direction !== null) {
                sortedGroupData.sort((a, b) => {
                    let aValue = a[sortConfig.key];
                    let bValue = b[sortConfig.key];

                    if (sortConfig.key === 'SkillsFormatted') {
                        try {
                            let tempA = a.AgentSkills || ""
                            let tempB = b.AgentSkills || ""
                            if (tempA.length > 0) {
                                aValue = getLengthOfCommaSeparatedValues(tempA)
                            }
                            if (tempB.length > 0) {
                                bValue = getLengthOfCommaSeparatedValues(tempB)
                            }
                        } catch (error) {
                            console.error('Error in sortData skills:', error);
                        }
                    }

                    if (typeof aValue === 'object' && aValue !== null) {
                        aValue = aValue.value || '';
                    }
                    if (typeof bValue === 'object' && bValue !== null) {
                        bValue = bValue.value || '';
                    }
                    if (!isNaN(Number(aValue)) && !isNaN(Number(bValue))) {
                        aValue = Number(aValue);
                        bValue = Number(bValue);
                    } else if (typeof aValue === 'string' && typeof bValue === 'string') {
                        aValue = aValue.toLowerCase();
                        bValue = bValue.toLowerCase();
                    }
                    if (aValue < bValue) {
                        return sortConfig.direction === 'ascending' ? -1 : 1;
                    }
                    if (aValue > bValue) {
                        return sortConfig.direction === 'ascending' ? 1 : -1;
                    }
                    return 0;
                });
            }
            return finalTableData.concat(sortedGroupData);
        } catch (error) {
            console.error('Error sorting group data:', error);
            return finalTableData.concat(data);
        }
    }

    const handleSort = (columnId: string) => {
        try {
            let direction: 'ascending' | 'descending' | null = 'ascending';

            if (sortConfig.key === columnId) {
                if (sortConfig.direction === 'ascending') {
                    direction = 'descending';
                } else if (sortConfig.direction === 'descending') {
                    direction = null;
                }
            }

            setSortConfig({ key: columnId, direction });
        } catch (error) {
            console.error('Error in handleSort:', error);
        }
    };

    const getSortIcon = (columnId: string) => {
        try {
            if (sortConfig.key !== columnId) return null;

            if (sortConfig.direction === 'ascending') {
                return <span style={{ marginLeft: '5px' }}>↑</span>;
            } else if (sortConfig.direction === 'descending') {
                return <span style={{ marginLeft: '5px' }}>↓</span>;
            }
            return null;
        } catch (error) {
            console.error("Error in getSortIcon", error);
            return null;
        }
    };

    return { sortData, handleSort, getSortIcon, getSortedGroupData };
}
